import type { WhoamiMe } from "@shared-domain/auth-service/contract";
import {
  canAddDictionaryEntry,
  deptRoleHasCapability,
  hasSystemCapability,
  type DeptCapability,
  isSystemRoleAtLeast,
  type RoleSplitIndependentCapability,
  type SystemCapability,
  type SystemRole,
} from "@shared-domain/unizo/permissions";

/**
 * PRODUCT_DEV-748: フロントの権限判定。もとは `components/shared/AdminLayout.tsx` に
 * 同居していたが、**純粋な判定ロジックなのに管理者画面レイアウトを引き連れてくる**
 * のが問題だった（メモ画面や ToDo タブも admin 判定を使うため、Minizo の dist にまで
 * 管理者画面のレイアウトが入ってしまう）。
 *
 * AdminLayout 側にあった `react-refresh/only-export-components` の disable も、
 * 「コンポーネントのファイルから非コンポーネントを export している」ことが原因なので
 * ここへ移すと不要になる。
 *
 * 判定そのものは移動前と同一（改ざんは許容。サーバ側の認可が正）。
 */

/** 現在のテナント・プロダクトのメンバーシップ。無ければ undefined。 */
const currentMembership = (auth: WhoamiMe) => {
  if (!auth || !auth.currentTenantId || !auth.info) {
    return undefined;
  }
  const tenantId = auth.currentTenantId;
  const productId = auth.info.productId;
  return auth.memberships.find(
    (x) =>
      x.tenantId === tenantId && x.products.some((p) => p.id === productId),
  );
};

/**
 * PRODUCT_DEV-721 設計レビュー指摘3・5: **システム軸でそのことができるか**の出し分け。
 *
 * ⚠ 画面では `role === "admin"` と書かず、**何をしたいか**でここを呼ぶ。役割とできることの
 * 対応は `@shared-domain/unizo/permissions` の表にだけ書く（サーバの `lib/permissions` と同じ表）。
 *
 * ユーザー管理・データ出力のように機能フラグ `managerRole` で最低限の役割が変わるものは、
 * フラグの値（`useTenantFeatureFlag("managerRole")`）を必ず渡す（型で渡し忘れを止めてある）。
 *
 * ⚠ ここは**表示の出し分けだけ**。改ざんは許容で、サーバ側の認可が正。
 */
export function checkCan(
  auth: WhoamiMe,
  capability: RoleSplitIndependentCapability,
): boolean;
export function checkCan(
  auth: WhoamiMe,
  capability: SystemCapability,
  options: { roleSplit: boolean },
): boolean;
export function checkCan(
  auth: WhoamiMe,
  capability: SystemCapability,
  options?: { roleSplit: boolean },
): boolean {
  const membership = currentMembership(auth);
  if (!membership) return false;
  // フラグに関係なく決まるもの（オーバーロードで型が保証）は、どちらを渡しても同じ結果になる。
  // 省かれたときは OFF（出さない側）に倒す
  return hasSystemCapability(membership, capability, {
    roleSplit: options?.roleSplit ?? false,
  });
}

/**
 * システム軸の役割が `min` 以上か。**メニューを役割の段（admin 権限／sysadmin 権限）で
 * 分けて見せる**ときだけに使う。できるかどうかの判定は `checkCan`。
 */
export const checkSystemRoleAtLeast = (
  auth: WhoamiMe,
  min: SystemRole,
): boolean => {
  const membership = currentMembership(auth);
  return !!membership && isSystemRoleAtLeast(membership, min);
};

/**
 * PRODUCT_DEV-1108: 辞書に言葉を足せるか（閲覧者は足せない）。判定の本体はサーバと同じ `canAddDictionaryEntry`。
 * ⚠ ここは**表示の出し分けだけ**。
 */
export const checkCanAddDictionaryEntry = (auth: WhoamiMe): boolean => {
  const membership = currentMembership(auth);
  return !!membership && canAddDictionaryEntry(membership);
};

/** テナント管理者以上か（部の集計の出し分けで、フラグ OFF のテナントの基準に使う）。 */
export const checkRoleIsAdmin = (auth: WhoamiMe): boolean =>
  checkSystemRoleAtLeast(auth, "admin");

/** sysadmin か（admin + specialFlags["sysadmin"]）。 */
export const checkRoleIsSysadmin = (auth: WhoamiMe): boolean =>
  checkSystemRoleAtLeast(auth, "sysadmin");

/**
 * PRODUCT_DEV-721: **部の集計（管理者ビュー）を見せるか**の出し分け。
 *
 * 9/8 のプロダクト要件会議で権限を2軸に割った。業務の数字を見るのは **manager**（業務軸）で、
 * **admin（システム軸）は設定とデータを管理する人**であって数字を見る人ではない。
 * 機能フラグ ON のテナントは全員が部署に所属する前提で、未所属の枠を作らない（9/15 設計レビュー指摘2）。
 * サーバ側 `lib/permissions` の `canViewTeamSummary` と同じ切り方にしてある。
 *
 * ⚠ 切り替えは**テナント単位の機能フラグ `managerRole`**（`useTenantFeatureFlag`）。
 * OFF のテナントは今日とまったく同じ見え方でなければならない。
 *
 * ⚠ ここは**表示の出し分けだけ**。改ざんは許容で、サーバ側の認可が正。
 */
export const checkCanViewTeamSummary = (
  auth: WhoamiMe,
  ctx: { isManager: boolean; roleSplit: boolean },
): boolean =>
  // 機能フラグ `managerRole` が OFF のテナントは従来どおり admin 基準（回帰なし）
  ctx.roleSplit ? ctx.isManager : checkRoleIsAdmin(auth);

/**
 * PRODUCT_DEV-808: 基本設定に**「部のまとめができた」の行を出すか**（まとめを受け取る可能性がある人か）。
 *
 * まとめの宛先はサーバが部署の枠ごとに決める（`selectTeamSummaryRecipients`）: `managerRole` ON なら
 * 枠の manager だけ（manager が居ない枠には送らない・PRODUCT_DEV-721）、OFF なら admin。
 *
 * ⚠ ここは**表示の出し分けだけ**。
 */
export const checkReceivesTeamSummary = (
  auth: WhoamiMe,
  ctx: { isManager: boolean; roleSplit: boolean },
): boolean => (ctx.roleSplit ? ctx.isManager : checkRoleIsAdmin(auth));

/**
 * PRODUCT_DEV-721: **他人の日報にコメント欄を出すか**の出し分け。
 *
 * 機能フラグ `managerRole` が ON のテナントでは **manager** に出す（admin ではない）。
 * 日報の画面は、サーバが読むことを許した相手のものしか表示されないので、
 * **出し分けは管理者ビューと同じ条件でよい**（`checkCanViewTeamSummary`）。
 * どの部署の manager かはサーバ側 `canCommentOnMemberActivity` が見ている。
 *
 * ⚠ ここは**表示の出し分けだけ**。改ざんは許容で、サーバ側の認可が正。
 */
export const checkCanCommentOnMemberActivity = (
  auth: WhoamiMe,
  ctx: { isManager: boolean; roleSplit: boolean },
): boolean => checkCanViewTeamSummary(auth, ctx);

/**
 * PRODUCT_DEV-721 第2期④: **管理者ビューに入れるか**の出し分け。
 *
 * 管理者ビューには、業務の数字（活動サマリ＝manager）と、配信の設定（フォーマットと配信＝admin）が
 * 同居している。フラグ ON では manager でない admin は活動サマリを見られないが、**配信の設定は
 * この画面にしか無い**ので入口は開ける（中は「フォーマットと配信」だけを出す）。
 *
 * OFF のテナントは `checkCanViewTeamSummary` と同じ（admin）。
 * 配信の設定を管理メニューへ移す案があり、移したらこの関数は要らなくなる。
 *
 * ⚠ ここは**表示の出し分けだけ**。改ざんは許容で、サーバ側の認可が正。
 */
export const checkCanOpenAdminView = (
  auth: WhoamiMe,
  ctx: { isManager: boolean; roleSplit: boolean },
): boolean =>
  checkCanViewTeamSummary(auth, ctx) || (ctx.roleSplit && checkRoleIsAdmin(auth));

/**
 * PRODUCT_DEV-1070: **いまの部署**で、その業務軸のことができるか。
 *
 * 部の数字・配信の設定は「いまの部署」のもの（PRODUCT_DEV-721 U6）。兼務している人が manager をしていない
 * 部署を選んでいるあいだは、ほかの部署で manager でも false。`checkCanViewTeamSummary` などの `isManager` には
 * **これの結果を渡す**（人単位の `isManager` をそのまま渡すと、部署を切り替えても入口や HOME が残る。2026-10-08 dev で踏んだ）。
 * 部署ごとの役割を返さない古いサーバでは、人単位の `isManager`（どこかの部署で manager）に倒す。
 *
 * ⚠ ここは**表示の出し分けだけ**。改ざんは許容で、サーバ側の認可が正。
 */
export const checkCanInActiveDept = (
  activeDept: { deptRole?: string } | null | undefined,
  isManager: boolean,
  capability: DeptCapability,
): boolean =>
  activeDept?.deptRole ? deptRoleHasCapability(activeDept.deptRole, capability) : isManager;

/**
 * PRODUCT_DEV-1070: **マネージャー画面への切替（名前の行の右端の「⇄ マネージャー画面」）を出すか**。
 *
 * 入口は manager だけ（2026-10-09 日暮さん決定）。manager でない admin には出さない（配信の設定は
 * URL `/mypage/admin` から入る）。**入室の判定は `checkCanOpenAdminView`（admin も入れる）のまま**で、別物。
 * `isManager` には `checkCanInActiveDept(…, "team.view")` の結果を渡す。
 * `managerRole` OFF のテナントは従来どおり admin（`checkCanViewTeamSummary` と同じ切り方）。
 *
 * ⚠ ここは**表示の出し分けだけ**。改ざんは許容で、サーバ側の認可が正。
 */
export const checkCanOpenManagerSwitch = (
  auth: WhoamiMe,
  ctx: { isManager: boolean; roleSplit: boolean },
): boolean => checkCanViewTeamSummary(auth, ctx);
